import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {ExpiredTokenError, SpringRestService} from '@aiknow-srl/aik-ngx-http';
import {AuthStateService} from './auth-state.service';
import {GrpcAuthorizationOPEService} from '../ope/grpc/authorizationOPE.service';
import {ToastService} from '../toast.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private authState = inject(AuthStateService);
  private be = inject(SpringRestService);
  private authorizationService = inject(GrpcAuthorizationOPEService);
  private toastService = inject(ToastService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.endsWith('.json') || request.url.endsWith('.svg')) {
      return next.handle(request);
    }

    const temporaryToken = this.authState.consumeTemporaryToken();
    if (temporaryToken) {
      return next.handle(this.withToken(request, temporaryToken));
    }

    const impersonationToken = sessionStorage.getItem('impersonationToken');
    if (impersonationToken) {
      return next.handle(this.withToken(request, impersonationToken)).pipe(
        catchError(error => {
          // Never resend as the session user: the request was meant for the impersonated one
          if (this.isUnauthorized(error) && sessionStorage.getItem('impersonationToken') === impersonationToken) {
            this.endImpersonation();
          }
          return throwError(() => error);
        })
      );
    }

    return this.sendAsSessionUser(request, next);
  }

  private sendAsSessionUser(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authState.getAccessToken();
    if (!token) {
      return next.handle(request);
    }
    return next.handle(this.withToken(request, token)).pipe(
      catchError(error => {
        if (!this.isUnauthorized(error)) {
          return throwError(() => error);
        }
        if (!this.authState.hasSession()) {
          // The session ended while this request was in flight: logout and redirect already happened
          return throwError(() => new ExpiredTokenError());
        }
        const current = this.authState.getAccessToken();
        const renewed$ = current && current !== token ? of(current) : this.authState.refresh();
        return renewed$.pipe(
          switchMap(newToken => next.handle(this.withToken(request, newToken)))
        );
      })
    );
  }

  private withToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const headers: Record<string, string> = {Authorization: `Bearer ${token}`, Accept: 'application/json'};
    if (!(request.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
      const language = this.be.getLanguage();
      if (language !== undefined) {
        headers['Accept-Language'] = language;
      }
    }
    return request.clone({setHeaders: headers});
  }

  private isUnauthorized(error: any): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  private endImpersonation(): void {
    this.authorizationService.stopImpersonation();
    document.documentElement.style.removeProperty('--impersonation-banner-height');
    this.authorizationService.impersonationEnded$.next();
    this.toastService.openTranslatedInfoToastMessage('IMPERSONATION.SESSION_EXPIRED');
  }
}
